import {
  Component,
  ElementRef,
  ViewChild,
  AfterViewChecked,
} from '@angular/core';
import { HttpService } from '../../store/http/http.service';
import {
  QuestionnaireTurn,
  QuestionnaireQuestion,
  VideoSection,
} from './chatbot.types';
import { ActivatedRoute } from '@angular/router';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { APIConfig } from '../../api/api-config.provider';

@Component({
  selector: 'app-user-story-chatbot',
  templateUrl: './user-story-chatbot.component.html',
  styleUrls: ['./user-story-chatbot.component.scss'],
})
export class UserStoryChatbotComponent implements AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  /** The full questionnaire turns history */
  questionnaireTurns: QuestionnaireTurn[] = [];

  /** The resolved video structure after completion */
  videoStructure: VideoSection[] | null = null;

  /** The current user input */
  userInput: string = '';

  /** Loading state while waiting for backend response */
  isLoading: boolean = false;

  /** Flag to disable further input after an abort */
  conversationAborted: boolean = false;

  /** Flag to track if we should scroll to bottom */
  private shouldScrollToBottom: boolean = false;

  /** Current chatbot response text */
  chatbotText: string = '';

  /** Current multi-choice questions from the chatbot */
  currentQuestions: QuestionnaireQuestion[] = [];

  customToken$: Observable<string>;
  customToken: string;
  constructor(
    private readonly httpService: HttpService,
    private route: ActivatedRoute,
    private readonly apiConfig: APIConfig,
  ) {
    this.customToken$ = this.route.paramMap.pipe(
      map(pm => pm.get('customToken') || ''),
    );

    this.customToken$.subscribe(token => {
      if (token) {
        if (token == this.customToken) {
          return;
        }
        // -- // -- // -- // -- //
        this.customToken = token;
        this.loadProject(token);
      } else {
        this.customToken = null;
      }
    });
  }

  loadProject(token: string) {
    console.log('load-project', token);
    console.log('load-project > customToken', this.customToken);
    this.httpService.httpClient
      .get(`${this.apiConfig.api.url}/customproject`, {
        headers: {
          Authorization: token,
        },
      })
      .subscribe({
        next: (response: any) => {
          console.log('response', response);
        },
        error: (error: any) => {
          console.error('Error loading project:', error);
        },
      });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  /**
   * Sends the user's message and processes the backend response.
   */
  sendMessage(): void {
    const trimmedInput = this.userInput.trim();

    // Guard clauses
    if (this.conversationAborted || !trimmedInput || this.isLoading) {
      return;
    }

    // Create a new turn with the user's answer
    const newTurn: QuestionnaireTurn = {
      text: '',
      answer: trimmedInput,
      multiChoiceQuestions: [],
      multiChoiceAnswers: {},
      proposedStructure: [],
    };
    this.questionnaireTurns.push(newTurn);
    this.userInput = '';
    this.isLoading = true;
    this.shouldScrollToBottom = true;

    // Call the backend
    this.httpService.httpClient
      .post(
        `${this.apiConfig.api.url}/customproject`,
        {
          questionnaireTurns: this.questionnaireTurns,
        },
        {
          headers: {
            Authorization: this.customToken,
          },
        },
      )
      .subscribe({
        next: (response: unknown) => {
          this.handleResponse(response as QuestionnaireTurn);
          this.isLoading = false;
          this.shouldScrollToBottom = true;
        },
        error: (error: unknown) => {
          console.error('Chatbot API error:', error);
          this.chatbotText = 'Sorry, something went wrong. Please try again.';
          this.currentQuestions = [];
          this.isLoading = false;
          this.shouldScrollToBottom = true;
        },
      });
  }

  /**
   * Submits the answers to the current multi-choice questions.
   */
  submitAnswers(): void {
    if (this.isLoading || !this.canSubmitAnswers()) {
      return;
    }

    // Build multiChoiceAnswers record from current questions
    const multiChoiceAnswers: Record<string, string> = {};
    this.currentQuestions.forEach(q => {
      multiChoiceAnswers[q.text] = q.answer;
    });

    // Update the last turn with the answers
    if (this.questionnaireTurns.length > 0) {
      const lastTurn =
        this.questionnaireTurns[this.questionnaireTurns.length - 1];
      lastTurn.multiChoiceAnswers = multiChoiceAnswers;
    }

    this.isLoading = true;
    this.shouldScrollToBottom = true;

    // Send to backend with updated turns
    this.httpService.httpClient
      .post(
        `${this.apiConfig.api.url}/customproject`,
        {
          questionnaireTurns: this.questionnaireTurns,
        },
        {
          headers: {
            Authorization: this.customToken,
          },
        },
      )
      .subscribe({
        next: (response: unknown) => {
          this.handleResponse(response as QuestionnaireTurn);
          this.isLoading = false;
          this.shouldScrollToBottom = true;
        },
        error: (error: unknown) => {
          console.error('Chatbot API error:', error);
          this.chatbotText = 'Sorry, something went wrong. Please try again.';
          this.currentQuestions = [];
          this.isLoading = false;
          this.shouldScrollToBottom = true;
        },
      });
  }

  /**
   * Checks if at least one question has an answer (or always allow submit).
   */
  canSubmitAnswers(): boolean {
    // Allow submitting even if not all questions are answered
    return true;
  }

  /**
   * Handles the chatbot response (a new QuestionnaireTurn).
   */
  private handleResponse(response: QuestionnaireTurn): void {
    // Store the text message
    this.chatbotText = response.text || '';

    // Add the new turn to the history
    this.questionnaireTurns.push(response);

    // Set up the multi-choice questions with initial state
    this.currentQuestions = (response.multiChoiceQuestions || []).map(q => ({
      ...q,
      answer: q.answer || '',
    }));

    // Check if there's a proposed structure (conversation finished)
    if (response.proposedStructure && response.proposedStructure.length > 0) {
      this.videoStructure = response.proposedStructure;
    }
  }

  /**
   * Selects an option for a question.
   */
  selectOption(question: QuestionnaireQuestion, option: string): void {
    question.answer = option;
  }

  /**
   * Checks if a specific option is selected for a question.
   */
  isOptionSelected(question: QuestionnaireQuestion, option: string): boolean {
    return question.answer === option;
  }

  /**
   * Handles the "I don't know" button click.
   * Sends a predefined message to start the conversation with guidance.
   */
  onIdontKnow(): void {
    this.userInput = "I don't know what story to create. Can you help me?";
    this.sendMessage();
  }

  /**
   * Handles keyboard events on the input field.
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  /**
   * Scrolls the messages container to the bottom.
   */
  private scrollToBottom(): void {
    try {
      const el = this.messagesContainer?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  /**
   * Stub method for video upload clicks.
   */
  onUploadVideo(section: VideoSection, mediaIndex: number): void {
    console.log('Upload video requested:', { section, mediaIndex });
  }

  /**
   * Stub method for image upload clicks.
   */
  onUploadImage(section: VideoSection, mediaIndex: number): void {
    console.log('Upload image requested:', { section, mediaIndex });
  }

  /**
   * Formats section type for display (capitalizes first letter).
   */
  formatSectionType(type: string): string {
    return type.charAt(0).toUpperCase() + type.slice(1);
  }
}
